import { FileSpreadsheet, Upload, X } from "lucide-react"
import { type ChangeEvent, useId, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { countDataRows, formatFileSize } from "@/lib/csv"
import { cn } from "@/lib/utils"

interface CsvUploadFieldProps {
  /** Field name on the Inertia <Form> POST. Backend reads `params[:file]`. */
  name?: string
  /** Hard cap from backend (`max_rows` Inertia prop). Defaults to 500. */
  maxRows?: number
  /** Disable on processing / offline. */
  disabled?: boolean
  /** Called whenever the picked file changes (or is cleared). */
  onFileChange?: (file: File | null) => void
  /**
   * Surfaced under the dropzone — server-side errors from
   * `errors.file` (Inertia render-prop). Renders inside the same
   * `<Alert>` as the client-side cap/empty errors so SR users see
   * one error region per UX Spec §Accessibility.
   */
  serverError?: string | string[]
  /**
   * If set, the field skips its own client-side row-count check
   * (the parent already counted, e.g. on a remount). Default false —
   * the field counts on every selection.
   */
  skipClientCount?: boolean
}

type ClientErrorKind =
  | { kind: "too-many-rows"; count: number }
  | { kind: "empty-file" }
  | { kind: "not-csv"; name: string }

/**
 * CsvUploadField — file picker + template link + client-side
 * pre-flight checks.
 *
 * Pre-flight responsibilities:
 *   1. **Wrong file type** — rejects anything where `accept=".csv"`
 *      didn't catch (e.g. `.txt` renamed). Browser UX is the first
 *      filter; this is belt-and-braces for keyboard users who
 *      bypass the picker via drag-drop.
 *   2. **Empty file / header-only** — reads the file once via
 *      `countDataRows` and rejects when the count is 0. UX Spec
 *      §Pre-upload errors lists this as a distinct branch ("empty
 *      file" copy, not the generic "too many rows").
 *   3. **Row cap** — rejects when `count > maxRows`. The cap value
 *      comes from the `max_rows` Inertia prop so the server stays
 *      authoritative. The same check happens server-side; this is
 *      the cheap UX win that avoids the upload round-trip.
 *
 * Pre-flight does NOT validate per-row data — that's the import
 * service's responsibility. We surface a `<File>` selection only
 * after all three checks pass; the file is then bound to the
 * native `<input type="file">` (via the imperative ref) so the
 * surrounding Inertia `<Form>` carries it as multipart/form-data.
 *
 * **Why a controlled `File` mirror?** Inertia auto-detects `File`
 * inputs and ships multipart, so we keep the canonical state in
 * the `<input>` element itself. Our `selected` mirror is for
 * rendering the "Selected: foo.csv (12 KB) [Replace]" affordance
 * + for surfacing the pre-flight errors. We DON'T re-set the
 * input's `files` from JS — once the user picks, the input is the
 * source of truth.
 *
 * Locked QA testid contract:
 *   - `csv-upload-field` (wrapper)
 *   - `csv-template-link` (template anchor)
 *   - `csv-row-cap-error` (over-cap branch)
 *   - `csv-empty-error` (empty/header-only branch)
 *
 * Accessibility: native `<input type="file">` is the canonical
 * keyboard entry; the visible "Choose file" button is a sibling
 * `<label>` styled as a Button — SR users land on the input
 * directly via Tab. UX Spec §Accessibility forbids JS-only
 * dropzones.
 */
export function CsvUploadField({
  name = "file",
  maxRows = 500,
  disabled,
  onFileChange,
  serverError,
  skipClientCount = false,
}: CsvUploadFieldProps) {
  const { t } = useTranslation()
  const inputId = useId()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [selected, setSelected] = useState<File | null>(null)
  const [clientError, setClientError] = useState<ClientErrorKind | null>(null)
  const [counting, setCounting] = useState(false)

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    void handleChangeAsync(event)
  }

  async function handleChangeAsync(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null
    setClientError(null)
    if (!file) {
      setSelected(null)
      onFileChange?.(null)
      return
    }
    // Wrong file type — accept attribute is the first filter, this
    // catches `.txt` renamed-to-csv on systems that don't sniff MIME.
    if (!isLikelyCsv(file)) {
      setSelected(null)
      setClientError({ kind: "not-csv", name: file.name })
      // Also reset the underlying input so the same wrong file can
      // be re-picked if the user wants to test again.
      event.target.value = ""
      onFileChange?.(null)
      return
    }
    if (skipClientCount) {
      setSelected(file)
      onFileChange?.(file)
      return
    }
    setCounting(true)
    try {
      const count = await countDataRows(file)
      if (count === 0) {
        setSelected(null)
        setClientError({ kind: "empty-file" })
        event.target.value = ""
        onFileChange?.(null)
        return
      }
      if (count > maxRows) {
        setSelected(null)
        setClientError({ kind: "too-many-rows", count })
        event.target.value = ""
        onFileChange?.(null)
        return
      }
      setSelected(file)
      onFileChange?.(file)
    } finally {
      setCounting(false)
    }
  }

  function handleClear() {
    setSelected(null)
    setClientError(null)
    if (inputRef.current) inputRef.current.value = ""
    onFileChange?.(null)
    // Restore focus to the picker for keyboard users.
    inputRef.current?.focus()
  }

  // Server error always wins over a stale client error — once a POST
  // round-trips back with an `errors.file`, the client-side state has
  // already been validated and shouldn't shadow the authoritative reply.
  const serverErrorMessages = normaliseError(serverError)
  const hasServerError = serverErrorMessages.length > 0
  const showError = clientError !== null || hasServerError

  return (
    <div
      className="flex flex-col gap-3"
      data-testid="csv-upload-field"
      data-state={
        showError
          ? "error"
          : selected
            ? "selected"
            : counting
              ? "counting"
              : "idle"
      }
    >
      <Label htmlFor={inputId} className="text-sm font-medium">
        {t("candidates.bulk.pre_upload.choose_file")}
      </Label>

      <div
        className={cn(
          "flex flex-col items-stretch gap-3 rounded-md border border-dashed p-4 sm:flex-row sm:items-center",
          showError && "border-destructive bg-destructive/5",
          selected && "border-primary bg-primary/5",
        )}
      >
        <FileSpreadsheet
          className="text-muted-foreground hidden size-6 sm:block"
          aria-hidden="true"
        />

        <div className="flex flex-1 flex-col gap-1">
          {selected ? (
            <span className="text-sm">
              {t("candidates.bulk.pre_upload.chosen_file", {
                filename: selected.name,
                size: formatFileSize(selected.size),
              })}
            </span>
          ) : (
            <span className="text-muted-foreground text-sm">
              {t("candidates.bulk.pre_upload.helper")}
            </span>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {/*
             * The <input> is visually hidden but keyboard-accessible — it's
             * focusable via Tab from the surrounding Form, and the visible
             * Button-styled label triggers it on click. SR users land on
             * the input itself with the right `aria-describedby` chain.
             */}
            <input
              ref={inputRef}
              id={inputId}
              name={name}
              type="file"
              accept=".csv,text/csv"
              onChange={handleChange}
              disabled={disabled}
              className="sr-only"
              aria-describedby={
                showError ? `${inputId}-error` : `${inputId}-helper`
              }
              aria-invalid={showError ? "true" : undefined}
            />
            {/*
             * UX Lead sign-off: touch targets ≥44px on mobile per
             * WCAG 2.5.5. shadcn Button `size="sm"` is h-8 (32px) —
             * `max-sm:h-11` bumps to 44px on < sm without touching
             * the shared shadcn primitive.
             */}
            <Button
              asChild
              type="button"
              variant="outline"
              size="sm"
              className="max-sm:h-11"
            >
              <label htmlFor={inputId} className="cursor-pointer">
                <Upload className="size-4" aria-hidden="true" />
                {selected
                  ? t("candidates.bulk.pre_upload.change_file")
                  : t("candidates.bulk.pre_upload.choose_file")}
              </label>
            </Button>
            {selected && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleClear}
                disabled={disabled}
                className="max-sm:h-11"
              >
                <X className="size-4" aria-hidden="true" />
                {t("candidates.add.cancel")}
              </Button>
            )}
          </div>
        </div>
      </div>

      <p id={`${inputId}-helper`} className="text-muted-foreground text-xs">
        {t("candidates.bulk.pre_upload.helper")}
      </p>

      {clientError !== null && (
        <Alert
          variant="destructive"
          id={`${inputId}-error`}
          data-testid={
            clientError.kind === "too-many-rows"
              ? "csv-row-cap-error"
              : clientError.kind === "empty-file"
                ? "csv-empty-error"
                : "csv-not-csv-error"
          }
          role="alert"
        >
          <AlertTitle>
            {clientError.kind === "too-many-rows"
              ? t("candidates.bulk.errors.too_many_rows.title")
              : clientError.kind === "empty-file"
                ? t("candidates.bulk.errors.empty_file.title")
                : t("candidates.bulk.errors.not_csv.title")}
          </AlertTitle>
          <AlertDescription>
            {clientError.kind === "too-many-rows"
              ? t("candidates.bulk.errors.too_many_rows.body", {
                  count: clientError.count,
                })
              : clientError.kind === "empty-file"
                ? t("candidates.bulk.errors.empty_file.body")
                : t("candidates.bulk.errors.not_csv.body", {
                    name: clientError.name,
                  })}
          </AlertDescription>
        </Alert>
      )}

      {hasServerError && (
        <Alert
          variant="destructive"
          id={
            clientError === null
              ? `${inputId}-error`
              : `${inputId}-server-error`
          }
          data-testid="csv-server-error"
          role="alert"
        >
          <AlertTitle>
            {t("candidates.bulk.errors.server_failure.title")}
          </AlertTitle>
          <AlertDescription>{serverErrorMessages.join(" ")}</AlertDescription>
        </Alert>
      )}
    </div>
  )
}

function isLikelyCsv(file: File): boolean {
  if (file.type === "text/csv" || file.type === "application/csv") return true
  // Some browsers + OSes drop the type for `.csv`. Fall back to the extension.
  return /\.csv$/i.test(file.name)
}

function normaliseError(input: string | string[] | undefined): string[] {
  if (!input) return []
  if (Array.isArray(input)) return input.filter(Boolean)
  return [input]
}
